/**
 * crystalCavePipeline — recursive 3D crystal cave.
 *
 * A raymarched scene of overlapping crystal SDFs (octahedra, prisms,
 * truncated shapes) arranged in a "cave" composition. Light reflects
 * off the crystal facets. Colors cycle through pastel hues. Audio
 * drives the crystal rotation and color palette.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface CrystalCavePipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float bass;
float mid;
float high;

// Forward declaration for hash21 (used by map()).
float hash21(vec2 p);

float sdSphere(vec3 p, float r) {
  return length(p) - r;
}

float sdBox(vec3 p, vec3 b) {
  vec3 q = abs(p) - b;
  return length(max(q, 0.0)) + min(max(q.x, max(q.y, q.z)), 0.0);
}

float sdOctahedron(vec3 p, float s) {
  p = abs(p);
  return (p.x + p.y + p.z - s) * 0.57735027;
}

mat2 rot2(float a) {
  float c = cos(a), s = sin(a);
  return mat2(c, -s, s, c);
}

// Rotate a 3D point around an arbitrary axis.
vec3 rotateAxis(vec3 p, vec3 axis, float angle) {
  float c = cos(angle), s = sin(angle);
  return p * c + cross(axis, p) * s + axis * dot(axis, p) * (1.0 - c);
}

// Scene: a cluster of crystals at various positions.
float map(vec3 p) {
  // Slowly rotate the scene.
  p.xz = rot2(uTime * 0.15) * p.xz;
  // Audio: stretch with bass.
  p *= 1.0 - bass * 0.05;
  float d = 100.0;
  // Central crystal.
  vec3 p1 = p;
  p1.xz = rot2(uTime * 0.3) * p1.xz;
  d = min(d, sdOctahedron(p1, 0.7));
  // Surrounding crystals.
  for (int i = 0; i < 6; i++) {
    float fi = float(i);
    float angle = fi * 6.2831853 / 6.0;
    vec3 offset = vec3(cos(angle), sin(fi * 2.7) * 0.3, sin(angle)) * 1.2;
    vec3 p2 = p - offset;
    // Rotate each crystal differently.
    p2.yz = rot2(fi * 1.5 + uTime * 0.4) * p2.yz;
    float s = 0.35 + 0.1 * hash21(vec2(fi, 1.0));
    d = min(d, sdOctahedron(p2, s));
  }
  return d;
}

vec3 calcNormal(vec3 p) {
  vec2 e = vec2(0.002, 0.0);
  return normalize(vec3(
    map(p + e.xyy) - map(p - e.xyy),
    map(p + e.yxy) - map(p - e.yxy),
    map(p + e.yyx) - map(p - e.yyx)
  ));
}

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}



void main() {
  bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  mid = (uMid.x + uMid.y + uMid.z) / 3.0;
  high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid = pow(clamp(mid * 3.0, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 4.0, 0.0, 1.0), 0.7);

  // Aspect-corrected screen coords.
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  // Camera.
  vec3 ro = vec3(0.0, 0.0, 2.5);
  vec3 ta = vec3(0.0, 0.0, 0.0);
  vec3 cw = normalize(ta - ro);
  vec3 cu = normalize(cross(cw, vec3(0.0, 1.0, 0.0)));
  vec3 cv = cross(cu, cw);
  vec3 rd = normalize(p.x * cu + p.y * cv + 1.5 * cw);

  // Raymarch.
  float td = 0.0;
  bool hit = false;
  vec3 hitP = ro;
  for (int i = 0; i < 80; i++) {
    vec3 p2 = ro + rd * td;
    float d = map(p2);
    if (d < 0.005) { hit = true; hitP = p2; break; }
    td += d * 0.9;
    if (td > 8.0) break;
  }

  vec3 col;
  if (hit) {
    vec3 n = calcNormal(hitP);
    // Diffuse lighting from a key light (cyan, top-right).
    vec3 lightDir = normalize(vec3(0.5, 1.0, 0.7));
    float diff = max(dot(n, lightDir), 0.0);
    // Fill light (magenta, bottom-left).
    vec3 fillDir = normalize(vec3(-0.6, -0.3, 0.5));
    float fill = max(dot(n, fillDir), 0.0);
    // Specular.
    vec3 v = normalize(ro - hitP);
    vec3 h = normalize(lightDir + v);
    float spec = pow(max(dot(n, h), 0.0), 64.0);
    // Fresnel rim.
    float fresnel = pow(1.0 - max(dot(n, v), 0.0), 4.0);
    // Base color: pastel mix.
    vec3 c1 = vec3(0.85, 0.45, 0.95);  // magenta
    vec3 c2 = vec3(0.40, 0.95, 0.85);  // cyan
    vec3 baseColor = mix(c1, c2, sin(uTime * 0.3) * 0.5 + 0.5);
    // Per-crystal hue (sample by position).
    float hueVar = sin(hitP.x * 3.0 + hitP.y * 2.0 + hitP.z * 4.0);
    baseColor = mix(baseColor, vec3(0.95, 0.85, 0.55), 0.5 + 0.5 * hueVar);
    // Audio: brighten.
    baseColor *= 1.0 + high * 0.3;
    col = baseColor * (0.15 + 0.7 * diff + fill * 0.3);
    col += vec3(1.0) * spec * 1.5;
    // Magenta rim light.
    col += vec3(1.0, 0.4, 0.85) * fresnel * 0.7;
    // Internal glow (high frequency makes it pulse).
    col += baseColor * high * 0.4;
  } else {
    // Background: deep gradient with subtle nebula.
    float bg = sin(p.x * 3.0 + uTime * 0.2) * 0.5 + 0.5;
    col = mix(vec3(0.05, 0.04, 0.10), vec3(0.10, 0.06, 0.18), bg);
  }

  // Vignette.
  float vig = 1.0 - dot(p, p) * 0.3;
  col *= clamp(vig, 0.0, 1.0);

  // Tone mapping.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}
`;

export function computeCrystalCaveBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.3 * Math.sin(t * 0.5);
  out[1] = 0.5 + 0.3 * Math.sin(t * 0.7 + 1.0);
  out[2] = 0.4 + 0.3 * Math.sin(t * 1.0 + 2.0);
  out[3] = 0.5 + 0.3 * Math.sin(t * 1.2 + 1.5);
  out[4] = 0.5 + 0.3 * Math.sin(t * 1.4 + 0.5);
  out[5] = 0.4 + 0.3 * Math.sin(t * 1.7 + 2.5);
  out[6] = 0.3 + 0.4 * Math.sin(t * 2.1 + 0.7);
  out[7] = 0.4 + 0.3 * Math.sin(t * 2.5 + 1.8);
  out[8] = 0.3 + 0.4 * Math.sin(t * 3.0 + 3.0);
}

export function computeCrystalCaveRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const ranges = [
    [0.00, 0.030], [0.030, 0.080], [0.080, 0.150],
    [0.150, 0.260], [0.260, 0.380], [0.380, 0.500],
    [0.500, 0.660], [0.660, 0.820], [0.820, 0.980],
  ];
  for (let i = 0; i < 9; i++) {
    const [lo, hi] = ranges[i];
    const start = Math.floor(lo * N);
    const end = Math.floor(hi * N);
    let sum = 0, count = 0;
    for (let k = start; k < end; k++) {
      sum += freqData[k];
      count++;
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createCrystalCavePipeline(gl: WebGL2RenderingContext): CrystalCavePipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);
  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawCrystalCave(
  gl: WebGL2RenderingContext,
  pipe: CrystalCavePipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  const prog = pipe.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
